import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Testimonials } from '../components/landing/Testimonials';
import { FAQ } from '../components/landing/FAQ';
import { HowItWorks } from '../components/landing/HowItWorks';
import { SeoMeta } from '../components/seo/SeoMeta';

export function Home() {
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState('');
  const [surname, setSurname] = useState('');

  const handleQuickGenerate = (e: React.FormEvent) => {
    e.preventDefault();
    if (firstName.trim() && surname.trim()) {
      // 跳转到名字生成器页面并传递参数
      navigate('/chinese-name-generator', { 
        state: { 
          firstName: firstName.trim(), 
          surname: surname.trim(),
          autoGenerate: true 
        } 
      });
    }
  };

  return (
    <>
      <SeoMeta
        title="Chinese Name - Discover Yours Today | Chinese-Name.net"
        description="Chinese name generator with meanings, pinyin, and cultural guidance. Discover your ideal Chinese name, compare styles, and create a name that fits."
        pathname="/"
      />
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-indigo-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <h1 className="text-4xl font-extrabold text-gray-900 sm:text-5xl md:text-6xl">
              <span className="block">Chinese Name</span>
              <span className="block text-indigo-600">Discover Your Perfect Chinese Name</span>
            </h1>
            <p className="mt-3 max-w-md mx-auto text-base text-gray-500 sm:text-lg md:mt-5 md:text-xl md:max-w-3xl">
              Transform your name into authentic Chinese with AI-powered generator. Get names with meanings, pinyin pronunciation, and cultural significance.
            </p>

            {/* Quick Generate Form */}
            <div className="mt-10 max-w-lg mx-auto">
              <form onSubmit={handleQuickGenerate} className="bg-white rounded-lg shadow-lg p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="home-firstName" className="block text-sm font-medium text-gray-700 mb-1 text-left">
                      First Name
                    </label>
                    <input
                      type="text"
                      id="home-firstName"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Your first name"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="home-surname" className="block text-sm font-medium text-gray-700 mb-1 text-left">
                      Last Name
                    </label>
                    <input
                      type="text"
                      id="home-surname"
                      value={surname}
                      onChange={(e) => setSurname(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Your last name"
                      required
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={!firstName.trim() || !surname.trim()}
                  className="w-full inline-flex justify-center items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                  🎯 Generate My Chinese Name
                </button>
              </form>
              
              <p className="mt-4 text-sm text-gray-500 text-center">
                ⭐ Join 150,000+ people with Chinese names
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Chinese ai name Section */}
      <section className="py-12 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Chinese ai name</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our AI Chinese name process turns your English name into a polished Chinese ai name within seconds, matching sound, meaning, and cultural tone.
            </p>
            <div className="mt-8 grid gap-6 md:grid-cols-2">
              <div className="bg-indigo-50 rounded-xl p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Generate Chinese names instantly</h3>
                <p className="text-gray-600">
                  Instant suggestions let you try dozens of combinations until you find the perfect vibe—modern, literary, or timeless.
                </p>
              </div>
              <div className="bg-indigo-50 rounded-xl p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Chinese name meaning</h3>
                <p className="text-gray-600">
                  Every suggestion explains the Chinese name meaning so you know what each character represents and why the pair works.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Generate Chinese names instantly Section */}
      <section className="py-10 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-3">Generate Chinese names instantly</h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Type your English name, choose a tone, and the generator delivers instant Chinese names that balance sound, rhythm, and meaning.
          </p>
        </div>
      </section>

      {/* Chinese name generator for foreigners Section */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Chinese name generator for foreigners</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-6">
              Designed for English speakers who want a natural Chinese name, including students, professionals, and digital nomads.
            </p>
            <div className="grid gap-6 md:grid-cols-2">
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Chinese names into English</h3>
                <p className="text-gray-600">
                  See how your Chinese characters read back into English so you can share the story with friends and family.
                </p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">My name in Chinese</h3>
                <p className="text-gray-600">
                  We answer “my name in Chinese” by pairing pronunciation, tonality, and desired meaning to deliver an authentic result.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* My name in Chinese Section */}
      <section className="py-12 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">My name in Chinese</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Discover how your English name appears in Chinese characters, along with explanations of each component.
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            <div className="bg-indigo-50 rounded-xl p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Names in Chinese and meanings</h3>
              <p className="text-gray-600">
                We reveal the meanings behind your Chinese name so you can describe the story whenever someone asks “what is my Chinese name”.
              </p>
            </div>
            <div className="bg-indigo-50 rounded-xl p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Chinese name translation</h3>
              <p className="text-gray-600">
                Compare the translation in reverse by seeing how the characters look in English, clarifying how “my name is in Chinese”.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Chinese name meaning Section */}
      <section className="py-10 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Chinese name meaning</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Understand what each Chinese name means so you can confidently explain “what is my Chinese name” to anyone.
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">What is my Chinese name</h3>
              <p className="text-gray-600">
                We break down each character to reveal its meaning, tone, and cultural symbolism so you know why the name works.
              </p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Names in Chinese and meanings</h3>
              <p className="text-gray-600">
                Compare multiple suggestions side-by-side to see how names in Chinese and meanings shift with different tones or virtues.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Function Navigation Section */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
              Explore Chinese Names by Category
            </h2>
            <p className="mt-4 text-xl text-gray-500">
              Browse authentic Chinese names organized by category
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 gap-8 md:grid-cols-3">
            {/* Boy Names Card */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="text-center">
                <div className="text-4xl mb-4">👦</div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Boy Names</h3>
                <p className="text-sm text-gray-600 mb-4">8,100+ monthly searches</p>
                <p className="text-base text-gray-700 mb-6">
                  Browse 200+ authentic Chinese boy names with meanings, from traditional classics to modern favorites.
                </p>
                <Link
                  to="/chinese-boy-names"
                  className="block w-full bg-indigo-600 text-white px-6 py-3 rounded-md hover:bg-indigo-700 transition-colors font-medium"
                >
                  Explore Chinese Boy Names with Meanings
                </Link>
              </div>
            </div>

            {/* Girl Names Card */}
            <div className="bg-gradient-to-br from-pink-50 to-rose-50 rounded-lg p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="text-center">
                <div className="text-4xl mb-4">👧</div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Girl Names</h3>
                <p className="text-sm text-gray-600 mb-4">6,600+ monthly searches</p>
                <p className="text-base text-gray-700 mb-6">
                  Discover 200+ beautiful Chinese girl names with meanings, from cute and elegant to traditional classics.
                </p>
                <Link
                  to="/chinese-girl-names"
                  className="block w-full bg-pink-600 text-white px-6 py-3 rounded-md hover:bg-pink-700 transition-colors font-medium"
                >
                  Explore Chinese Girl Names by Style
                </Link>
              </div>
            </div>

            {/* Last Names Card */}
            <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-lg p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="text-center">
                <div className="text-4xl mb-4">📜</div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Last Names</h3>
                <p className="text-sm text-gray-600 mb-4">2,400+ monthly searches</p>
                <p className="text-base text-gray-700 mb-6">
                  Explore 100+ common Chinese surnames with origins, meanings, and cultural history.
                </p>
                <Link
                  to="/chinese-last-names"
                  className="block w-full bg-purple-600 text-white px-6 py-3 rounded-md hover:bg-purple-700 transition-colors font-medium"
                >
                  Explore Chinese Last Names and Origins
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Why Use Our Generator Section */}
      <div className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
              Why Use Our Chinese Name Tool?
            </h2>
          </div>

          <div className="mt-12 grid grid-cols-1 gap-8 md:grid-cols-3">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="text-center">
                <div className="text-3xl mb-4">✓</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">AI-Powered Accuracy</h3>
                <p className="text-base text-gray-600">
                  Advanced AI creates culturally appropriate names that respect traditional Chinese naming conventions.
                </p>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="text-center">
                <div className="text-3xl mb-4">✓</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Authentic Names</h3>
                <p className="text-base text-gray-600">
                  Real Chinese names with cultural significance, not random character combinations.
                </p>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="text-center">
                <div className="text-3xl mb-4">✓</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Complete Info</h3>
                <p className="text-base text-gray-600">
                  Pinyin pronunciation, meaning, and cultural significance explained for each name.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Popular Names Preview Section */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
              Popular Chinese Names
            </h2>
          </div>

          <div className="mt-12 grid grid-cols-1 gap-8 md:grid-cols-2">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Top Boy Names</h3>
              <ul className="space-y-2">
                <li className="text-gray-700"><strong>浩然</strong> (Hàorán) - Vast and upright</li>
                <li className="text-gray-700"><strong>子轩</strong> (Zǐxuān) - Noble son</li>
                <li className="text-gray-700"><strong>宇航</strong> (Yǔháng) - Cosmic navigator</li>
              </ul>
              <Link
                to="/chinese-boy-names"
                className="inline-block mt-4 text-indigo-600 hover:text-indigo-700 font-medium"
              >
                Browse the full Chinese boy names library
              </Link>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Top Girl Names</h3>
              <ul className="space-y-2">
                <li className="text-gray-700"><strong>雅婷</strong> (Yǎtíng) - Elegant and graceful</li>
                <li className="text-gray-700"><strong>欣怡</strong> (Xīnyí) - Joyful and pleasant</li>
                <li className="text-gray-700"><strong>梦琪</strong> (Mèngqí) - Dream jade</li>
              </ul>
              <Link
                to="/cute-chinese-girl-names"
                className="inline-block mt-4 text-pink-600 hover:text-pink-700 font-medium"
              >
                Explore cute Chinese girl names and meanings
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* AI-Powered Generation Section */}
      <div className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
              AI-Powered Chinese Name Generation
            </h2>
            <p className="mt-4 text-xl text-gray-500">
              Advanced technology meets cultural tradition to create authentic Chinese names.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 gap-8 lg:grid-cols-2">
            <div className="p-8 bg-white rounded-lg shadow-sm">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Get Your Chinese Name Card</h3>
              <p className="text-gray-600 mb-6">
                Download beautiful, shareable name cards featuring your Chinese name with pronunciation guides, character meanings, and cultural explanations.
              </p>
              <Link
                to="/chinese-name-generator"
                className="inline-block bg-indigo-600 text-white px-6 py-2 rounded-md hover:bg-indigo-700 transition-colors"
              >
                Create a personalized Chinese name card
              </Link>
            </div>
            
            <div className="p-8 bg-white rounded-lg shadow-sm">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Cultural Authenticity</h3>
              <p className="text-gray-600 mb-6">
                Our AI analyzes traditional Chinese naming patterns, ensuring every suggestion respects cultural traditions while being meaningful and memorable.
              </p>
              <Link
                to="/cultural-insights"
                className="inline-block bg-purple-600 text-white px-6 py-2 rounded-md hover:bg-purple-700 transition-colors"
              >
                Explore Chinese naming culture insights
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Authentic Chinese Names Section */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
              Authentic Chinese Names with Cultural Significance
            </h2>
            <p className="mt-4 text-xl text-gray-500">
              Experience the depth and beauty of Chinese naming traditions through our comprehensive generator.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 gap-8 md:grid-cols-2">
            <div className="text-center p-6">
              <div className="text-4xl font-bold text-indigo-600 mb-2">50,000+</div>
              <div className="text-xl font-semibold text-gray-900 mb-2">Names Generated</div>
              <div className="text-gray-600">Happy users worldwide have discovered their perfect Chinese names</div>
            </div>
            
            <div className="text-center p-6">
              <div className="text-4xl font-bold text-purple-600 mb-2">100+</div>
              <div className="text-xl font-semibold text-gray-900 mb-2">Character Combinations</div>
              <div className="text-gray-600">Extensive database of meaningful Chinese characters for authentic names</div>
            </div>
          </div>
        </div>
      </div>

      {/* How It Works Section */}
      <HowItWorks />

      {/* Testimonials Section */}
      <Testimonials />

      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Popular Search Paths on the Site</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Use these focused pages when you already know the type of Chinese name you want to explore.
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
            <Link to="/cute-chinese-girl-names" className="rounded-xl border border-pink-100 bg-pink-50 p-6 hover:shadow-md transition-shadow">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Cute Chinese Girl Names</h3>
              <p className="text-gray-600">Browse sweet, elegant, and youthful girl names with pinyin and character meanings.</p>
            </Link>
            <Link to="/funny-chinese-names" className="rounded-xl border border-amber-100 bg-amber-50 p-6 hover:shadow-md transition-shadow">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Funny Chinese Names</h3>
              <p className="text-gray-600">Find playful naming ideas while avoiding awkward or disrespectful combinations.</p>
            </Link>
            <Link to="/chinese-pet-names" className="rounded-xl border border-emerald-100 bg-emerald-50 p-6 hover:shadow-md transition-shadow">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Chinese Pet Names</h3>
              <p className="text-gray-600">Explore Chinese dog names and cat names that sound cute, lucky, and easy to call every day.</p>
            </Link>
            <Link to="/chinese-dragon-names" className="rounded-xl border border-sky-100 bg-sky-50 p-6 hover:shadow-md transition-shadow">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Chinese Dragon Names</h3>
              <p className="text-gray-600">Find mythical Chinese dragon names with powerful symbolism for stories, games, and fantasy worlds.</p>
            </Link>
            <Link to="/chinese-last-names" className="rounded-xl border border-purple-100 bg-purple-50 p-6 hover:shadow-md transition-shadow">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Chinese Last Names</h3>
              <p className="text-gray-600">Compare surname origins, meanings, and regional history before building a full name.</p>
            </Link>
          </div>
        </div>
      </section>

      {/* Chinese Name Section */}
      <section className="py-12 bg-gradient-to-r from-indigo-50 to-blue-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Chinese Name</h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            One tool, endless possibilities. Our Chinese Name Generator blends authenticity and long-tail storytelling so each page stays on intent.
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQ />

      {/* CTA Section */}
      <div className="bg-indigo-50">
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-extrabold text-gray-900">
            Ready to discover your perfect Chinese name?
          </h2>
          <p className="mt-4 text-lg text-gray-600">
            Join thousands of people who have found their meaningful Chinese names with our free generator.
          </p>
          <div className="mt-8">
            <Link
              to="/chinese-name-generator"
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Start generating your Chinese name now
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
